import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Encabezado } from '../navegacion/Marco';
import {
  Aviso,
  Boton,
  Cajon,
  Campo,
  Cargando,
  ErrorEnPantalla,
  Panel,
  Selector,
  Vacio,
  clases,
} from '../comun/componentes';
import { useAccion, useDatos } from '../comun/useDatos';
import { actualizar, enviar } from '../api/cliente';
import { fechaConDia, hoyIso, isoDe, sumarDias } from '../comun/formato';
import { useSesion } from '../sesion/Sesion';
import { EstadoDeCita, TONO_CITA } from './Inicio';
import { BuscadorDePaciente } from './FichaPaciente';
import type { AgendaDiaria, CitaAgenda } from '../api/tipos';

/** Horas que se dibujan en la grilla: cubren el horario partido de una clínica paraguaya. */
const DESDE_HORA = 7;
const HASTA_HORA = 20;

/**
 * Agenda del día por profesional. La grilla es la vista real de una clínica: una columna
 * por profesional, las horas a la izquierda y el bloque de cada cita donde va. En teléfono
 * se convierte en una lista cronológica, que es lo único que se lee bien en 360 px.
 */
export function Agenda() {
  const { puede } = useSesion();
  const [parametros, setParametros] = useSearchParams();
  const [dia, setDia] = useState(parametros.get('dia') ?? hoyIso());
  const [agendando, setAgendando] = useState(parametros.get('paciente') != null);

  const { datos, cargando, error, recargar } = useDatos<AgendaDiaria>(
    `/api/v1/agenda/dia?dia=${dia}`,
    [dia],
  );

  const profesionales = useMemo(() => {
    const mapa = new Map<string, string>();

    for (const cita of datos?.citas ?? []) {
      mapa.set(cita.profesionalId, cita.profesional);
    }

    return [...mapa.entries()].map(([id, nombre]) => ({ id, nombre }));
  }, [datos]);

  const irA = (nuevo: string) => {
    setDia(nuevo);
    setParametros((previos) => {
      const copia = new URLSearchParams(previos);
      copia.set('dia', nuevo);

      return copia;
    });
  };

  return (
    <>
      <Encabezado
        titulo="Agenda"
        detalle={fechaConDia(dia)}
        accion={
          puede('agenda.agendar') && (
            <Boton variante="primario" onClick={() => setAgendando(true)}>
              Agendar cita
            </Boton>
          )
        }
      />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Boton onClick={() => irA(sumarDias(dia, -1))} aria-label="Día anterior">
          ←
        </Boton>
        <input
          type="date"
          value={dia}
          onChange={(e) => irA(e.target.value)}
          className="campo w-auto"
          aria-label="Día de la agenda"
        />
        <Boton onClick={() => irA(sumarDias(dia, 1))} aria-label="Día siguiente">
          →
        </Boton>
        <Boton onClick={() => irA(hoyIso())}>Hoy</Boton>
      </div>

      {datos?.feriados.length ? (
        <div className="mb-4">
          <Aviso tono="aviso">Feriado: {datos.feriados.join(', ')}</Aviso>
        </div>
      ) : null}

      {cargando && <Cargando que="la agenda" />}
      {error && <ErrorEnPantalla mensaje={error} reintentar={recargar} />}

      {datos && datos.citas.length === 0 && (
        <Panel>
          <Vacio
            titulo="No hay citas este día"
            detalle={
              datos.esLaborable
                ? 'Agendá la primera: elegís paciente, profesional y hora, y listo.'
                : 'Es un día no laborable para la clínica.'
            }
            accion={
              puede('agenda.agendar') && (
                <Boton variante="primario" onClick={() => setAgendando(true)}>
                  Agendar cita
                </Boton>
              )
            }
          />
        </Panel>
      )}

      {datos && datos.citas.length > 0 && (
        <>
          {/* Teléfono: lista cronológica. */}
          <div className="space-y-2 portatil:hidden">
            {datos.citas.map((cita) => (
              <TarjetaDeCita key={cita.id} cita={cita} alCambiar={recargar} />
            ))}
          </div>

          {/* Tablet y escritorio: grilla por profesional. */}
          <div className="hidden portatil:block">
            <Grilla profesionales={profesionales} citas={datos.citas} alCambiar={recargar} />
          </div>
        </>
      )}

      <AgendarCita
        abierto={agendando}
        dia={dia}
        pacienteInicial={parametros.get('paciente')}
        profesionales={profesionales}
        alCerrar={() => setAgendando(false)}
        alAgendar={() => {
          setAgendando(false);
          recargar();
        }}
      />
    </>
  );
}

function Grilla({
  profesionales,
  citas,
  alCambiar,
}: {
  profesionales: { id: string; nombre: string }[];
  citas: CitaAgenda[];
  alCambiar: () => void;
}) {
  // 96 px por hora: así media hora —la cita más corta que agenda una clínica— mide 48 px y
  // se puede tocar con guante en la tablet, que es donde se usa esta pantalla.
  const alturaHora = 96;
  const alto = (HASTA_HORA - DESDE_HORA) * alturaHora;

  return (
    <div className="panel overflow-x-auto">
      <div className="flex min-w-[720px]">
        <div className="w-14 shrink-0 border-r border-papel-200 pt-8">
          {Array.from({ length: HASTA_HORA - DESDE_HORA }, (_, i) => (
            <div
              key={i}
              style={{ height: alturaHora }}
              className="cifra -mt-2 pr-2 text-right text-dato text-tinta-400"
            >
              {`${DESDE_HORA + i}`.padStart(2, '0')}:00
            </div>
          ))}
        </div>

        <div className="flex flex-1">
          {profesionales.map((profesional) => (
            <div key={profesional.id} className="min-w-0 flex-1 border-r border-papel-200 last:border-r-0">
              <p className="truncate border-b border-papel-200 px-2 py-2 text-dato font-semibold text-tinta-700">
                {profesional.nombre}
              </p>
              <div className="relative" style={{ height: alto }}>
                {Array.from({ length: HASTA_HORA - DESDE_HORA }, (_, i) => (
                  <div
                    key={i}
                    className="absolute inset-x-0 border-t border-papel-100"
                    style={{ top: i * alturaHora }}
                  />
                ))}

                {citas
                  .filter((cita) => cita.profesionalId === profesional.id)
                  .map((cita) => {
                    const hora = Number(cita.inicioLocal.slice(11, 13));
                    const minuto = Number(cita.inicioLocal.slice(14, 16));
                    const desplazamiento = (hora - DESDE_HORA + minuto / 60) * alturaHora;
                    const altura = Math.max((cita.duracionMin / 60) * alturaHora, 26);

                    return (
                      <Link
                        key={cita.id}
                        to={`/pacientes/${cita.pacienteId}`}
                        style={{ top: desplazamiento, height: altura }}
                        data-tactil=""
                        className={clases(
                          'absolute inset-x-1 overflow-hidden rounded-md border-l-4 px-2 py-1 text-dato',
                          colorDeCita(cita.estado),
                        )}
                      >
                        <span className="block truncate font-medium">
                          {cita.inicioLocal.slice(11, 16)} {cita.paciente}
                        </span>
                        {altura > 56 && cita.motivoConsulta && (
                          <span className="block truncate opacity-80">{cita.motivoConsulta}</span>
                        )}
                      </Link>
                    );
                  })}
              </div>
            </div>
          ))}
        </div>
      </div>

      <ul className="flex flex-wrap gap-2 border-t border-papel-200 px-3 py-2">
        {citas.slice(0, 0).map(() => null)}
        <li className="text-dato text-tinta-400">
          Tocá una cita para abrir la ficha del paciente.
        </li>
      </ul>

      <div className="border-t border-papel-200 p-3">
        <p className="etiqueta-campo mb-2">Cambiar estado</p>
        <div className="grid gap-2 tablet:grid-cols-2 escritorio:grid-cols-3">
          {citas
            .filter((cita) => cita.estado !== 'Atendida' && cita.estado !== 'Cancelada')
            .slice(0, 6)
            .map((cita) => (
              <TarjetaDeCita key={cita.id} cita={cita} alCambiar={alCambiar} compacta />
            ))}
        </div>
      </div>
    </div>
  );
}

// El color de cada bloque se deriva del mismo TONO_CITA que pinta la insignia de estado
// (Inicio.tsx): un solo lugar decide qué significa cada estado, y acá sólo se traduce ese
// tono al estilo de bloque de la grilla. Cancelada es la única excepción real: además del
// tono neutro, necesita el tachado que no tiene sentido en una insignia de texto.
const BLOQUE_POR_TONO: Record<'neutro' | 'marca' | 'exito' | 'aviso' | 'alerta', string> = {
  neutro: 'border-tinta-400 bg-white text-tinta-700',
  marca: 'border-marca-600 bg-marca-50 text-marca-700',
  exito: 'border-exito-500 bg-exito-50 text-exito-700',
  aviso: 'border-aviso-500 bg-aviso-50 text-aviso-700',
  alerta: 'border-alerta-500 bg-alerta-50 text-alerta-700',
};

function colorDeCita(estado: string): string {
  if (estado === 'Cancelada') {
    return 'border-papel-300 bg-papel-100 text-tinta-400 line-through';
  }

  return BLOQUE_POR_TONO[TONO_CITA[estado] ?? 'neutro'];
}

/** Estados a los que se puede pasar desde cada estado. Refleja la máquina del dominio. */
const SIGUIENTES: Record<string, string[]> = {
  Programada: ['Confirmada', 'EnSala', 'Cancelada', 'Ausente'],
  Confirmada: ['EnSala', 'Cancelada', 'Ausente'],
  EnSala: ['EnAtencion', 'Ausente'],
  EnAtencion: ['Atendida'],
};

function TarjetaDeCita({
  cita,
  alCambiar,
  compacta = false,
}: {
  cita: CitaAgenda;
  alCambiar: () => void;
  compacta?: boolean;
}) {
  const { puede } = useSesion();
  const { correr, ejecutando, error } = useAccion();
  const siguientes = SIGUIENTES[cita.estado] ?? [];

  const cambiar = async (estado: string) => {
    const hecho = await correr(() =>
      actualizar(`/api/v1/agenda/citas/${cita.id}/estado`, { estado }),
    );

    if (hecho !== null) {
      alCambiar();
    }
  };

  return (
    <div className={clases('panel p-3', compacta && 'shadow-none')}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="cifra text-sm font-semibold text-tinta-900">
            {cita.inicioLocal.slice(11, 16)} · {cita.duracionMin} min
          </p>
          <Link
            to={`/pacientes/${cita.pacienteId}`}
            className="block truncate text-sm text-tinta-700 hover:text-marca-700"
          >
            {cita.paciente}
          </Link>
          <p className="truncate text-dato text-tinta-500">
            {cita.profesional}
            {cita.sillon ? ` · ${cita.sillon}` : ''}
          </p>
        </div>
        <EstadoDeCita estado={cita.estado} />
      </div>

      {puede('agenda.reprogramar') && siguientes.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {siguientes.map((estado) => (
            <Boton
              key={estado}
              variante={estado === 'Ausente' || estado === 'Cancelada' ? 'peligro' : 'secundario'}
              disabled={ejecutando}
              onClick={() => void cambiar(estado)}
              className="px-3 text-dato"
            >
              {estado.replace(/([A-Z])/g, ' $1').trim()}
            </Boton>
          ))}
        </div>
      )}

      {error && <p className="mt-2 text-dato text-alerta-700">{error}</p>}
    </div>
  );
}

function AgendarCita({
  abierto,
  dia,
  pacienteInicial,
  profesionales,
  alCerrar,
  alAgendar,
}: {
  abierto: boolean;
  dia: string;
  pacienteInicial: string | null;
  profesionales: { id: string; nombre: string }[];
  alCerrar: () => void;
  alAgendar: () => void;
}) {
  const { correr, ejecutando, error, porCampo } = useAccion();
  const [pacienteId, setPacienteId] = useState(pacienteInicial ?? '');
  const [profesionalId, setProfesionalId] = useState(profesionales[0]?.id ?? '');
  const [hora, setHora] = useState('08:00');
  const [duracion, setDuracion] = useState(30);
  const [motivo, setMotivo] = useState('');
  const [forzar, setForzar] = useState(false);

  const agendar = async () => {
    const creada = await correr(() =>
      enviar<{ id: string }>('/api/v1/agenda/citas', {
        pacienteId,
        profesionalId,
        inicioLocal: `${dia}T${hora}:00`,
        duracionMin: duracion,
        motivoConsulta: motivo.trim() || null,
        forzar,
      }),
    );

    if (creada) {
      setMotivo('');
      setForzar(false);
      alAgendar();
    }
  };

  return (
    <Cajon
      abierto={abierto}
      titulo="Agendar cita"
      alCerrar={alCerrar}
      pie={
        <div className="flex gap-2">
          <Boton
            variante="primario"
            ancho
            cargando={ejecutando}
            disabled={!pacienteId || !profesionalId}
            onClick={() => void agendar()}
          >
            Agendar
          </Boton>
          <Boton onClick={alCerrar}>Cancelar</Boton>
        </div>
      }
    >
      <div className="space-y-4">
        <BuscadorDePaciente id="buscar-paciente" elegidoId={pacienteId} alElegir={(p) => setPacienteId(p.id)} />

        <Selector
          etiqueta="Profesional"
          value={profesionalId}
          error={porCampo.ProfesionalId?.[0]}
          onChange={(e) => setProfesionalId(e.target.value)}
        >
          <option value="">Elegí un profesional</option>
          {profesionales.map((profesional) => (
            <option key={profesional.id} value={profesional.id}>
              {profesional.nombre}
            </option>
          ))}
        </Selector>

        <div className="grid grid-cols-2 gap-3">
          <Campo
            etiqueta="Hora"
            type="time"
            step={300}
            value={hora}
            onChange={(e) => setHora(e.target.value)}
          />
          <Selector
            etiqueta="Duración"
            value={duracion}
            error={porCampo.DuracionMin?.[0]}
            onChange={(e) => setDuracion(Number(e.target.value))}
          >
            {[15, 30, 45, 60, 90, 120].map((minutos) => (
              <option key={minutos} value={minutos}>
                {minutos} min
              </option>
            ))}
          </Selector>
        </div>

        <Campo
          etiqueta="Motivo de consulta"
          value={motivo}
          placeholder="Control y limpieza"
          onChange={(e) => setMotivo(e.target.value)}
        />

        <p className="text-dato text-tinta-500">
          Día: <strong>{fechaConDia(dia)}</strong>. Para otro día, cambialo en la agenda antes de
          abrir este panel.
        </p>

        {error && (
          <div role="alert" className="space-y-2 rounded-lg bg-alerta-50 px-3 py-2">
            <p className="text-dato text-alerta-700">{error}</p>
            <label className="flex min-h-tactil cursor-pointer items-center gap-2 text-dato text-alerta-700">
              <input
                type="checkbox"
                checked={forzar}
                onChange={(e) => setForzar(e.target.checked)}
                className="h-5 w-5 rounded border-papel-300 text-marca-600 focus:ring-marca-600"
              />
              Agendar igual, asumiendo el conflicto
            </label>
          </div>
        )}
      </div>
    </Cajon>
  );
}

/** Se exporta para las pruebas: el día siguiente en ISO, respetando el huso local. */
export const proximoDia = (dia: string) => isoDe(new Date(`${dia}T12:00:00`));
